import React, { useCallback, useMemo, useState } from "react";
import classNames from "classnames";
import _ from "lodash";

import { getChunkedData, getErrorMessage, reArrangeGeneralPhotos, reArrangeInspectionItemAnomalies } from "../../../utils/utilities";
import { splitProject } from "../../../services/projectService";
import { generateCachePayload, store } from "../../../services/reportCache";

import AccordionItem from "../../custom-reports/custom-visual-inspection/accordion-item";
import Button from "../../themed/button";
import Confirm from "../../themed/confirm";
import ChunkReportForm from "../../forms/chunkReportForm";
import Modal from "../../themed/modal";

import styles from "./style.module.scss";
import toastService from "../../../services/toastService";

interface Props<T> {
    reportDetails: object;
    chunkKeyId: keyof T;
    groupName: string;
    items: T[];
    isNoticeVisible: boolean;
    projectId: string;
    projectModule: string;
    onCloseNotice: () => void;
    onCancelChunkReport: () => void;
    onSetChunkedItems: (chunk: T[]) => void;
    setFullPreviewVisible: () => void;
}

const ChunkReport = <T extends unknown>({  chunkKeyId, items, isNoticeVisible, groupName, onCancelChunkReport, onCloseNotice, onSetChunkedItems, projectId, projectModule, reportDetails, setFullPreviewVisible }: Props<T>) => {
    const [chunkSize, setChunkSize] = useState(-1);

    const chunks = useMemo(() => {
        if (chunkSize > 0) {
            const actualChunkSize = Math.ceil(items.length / chunkSize);
            const pipingItemChunks = getChunkedData(items, actualChunkSize);
    
            return pipingItemChunks;
        }

        return [];
    }, [chunkSize, items]);

    const getLabelFromChunkItem = useCallback((chunk: T[]) => {
        let firstItemLabel = chunk[0][chunkKeyId];
        let lastItemLabel = chunk[chunk.length - 1][chunkKeyId];

        return `(#${_.get(chunk[0], 'id')} ${firstItemLabel}) - (#${_.get(chunk[chunk.length - 1], 'id')} ${lastItemLabel})`;
    }, [chunkKeyId]);

    const handleCloseChunkSelect = useCallback(() => {
        setChunkSize(-1);
        setFullPreviewVisible();
    }, [setFullPreviewVisible]);

    const handleSplitReport = useCallback(async () => {
        const rearrangedChunks = chunks.map((chunk) => {
            const rearrangedAnomalies = reArrangeInspectionItemAnomalies(chunk) as any[];
            const rearrangedGeneralPhotos = reArrangeGeneralPhotos(rearrangedAnomalies);

            return rearrangedGeneralPhotos;
        });

        const saveCurrentProgress = async () => {
            return store(projectId, { ...reportDetails, [groupName]: rearrangedChunks[0] }, 'Report split');
        };

        const splitReport = async () => {
            const promises = [
                saveCurrentProgress(),
                splitProject({
                    projectId,
                    action: 'split',
                    data: rearrangedChunks.slice(1).map((chunk, index) => ({
                        documentNumberPrefix: `${projectModule.toUpperCase()}-PART-${index + 1}`, 
                        payload: generateCachePayload({ ...reportDetails, [groupName]: chunk }),
                    })),
                })
            ];

            await Promise.all(promises); 

            window.location.href = '/dashboard/projects';
        };

        toastService.promise(splitReport(), {
			loading: 'Splitting the report...',
			success: 'Report split successfully!',
			error: getErrorMessage,
		});
    }, [projectId, chunks, groupName, projectModule, reportDetails]);

    const handleChunkAction = (chunk: T[]) => {
        onSetChunkedItems(chunk);
        setChunkSize(-1);
        setFullPreviewVisible();
    };

    if (isNoticeVisible) {
        return ( 
			<Modal style={{ height: "auto" }} isVisible={isNoticeVisible} onClose={onCancelChunkReport}>
                <div className="p-2">
                    <Confirm
                        title="Chunk Report"
                        message="This is quite a large report. We suggest splitting this into smaller reports as you may have a hard time publishing this."
                        onCancel={onCancelChunkReport}
                        onConfirm={() => {
                            setChunkSize(0);
                            onCloseNotice();
                        }}
                    />
                </div>
            </Modal>
        );
    }

    if (chunkSize === 0) {
		return (
            <Modal style={{ height: "auto" }} isVisible={chunkSize === 0} onClose={handleCloseChunkSelect}>
                <ChunkReportForm onProceed={(chunkSize: number) => setChunkSize(chunkSize)} />;
            </Modal>
        );
    }

    if (chunks.length > 0) {
        return (
            <Modal isVisible={chunks.length > 0} onClose={handleCloseChunkSelect}>
                <div className="p-2">
                    {chunks.map((chunk, index) => (
                        <AccordionItem key={index} isActive title={`Part ${index + 1}: (${chunk.length} items)`} >
                            <div className={classNames([styles.chunkedItem, "d-flex justify-content-start align-items-center"])}>
                                <div className={classNames([styles.chunkedItemLabel, "flex-fill"])}>
                                    {getLabelFromChunkItem(chunk)}
                                </div>
    
                                <div>
                                    <Button
                                        label="Preview"
                                        icon="binoculars-fill" 
                                        style={{ marginTop: 0 }} 
                                        onClick={() => handleChunkAction(chunk)} 
                                    />
                                </div>
                            </div>
                        </AccordionItem>
                    ))}

                    <div className="d-flex justify-content-end align-items-center">
                        <Button
                            label="Split Reports"
                            icon="binoculars-fill" 
                            onClick={handleSplitReport} 
                        />
                    </div>
                </div>
            </Modal>
        );
    }

    return null;
};
 
export default ChunkReport;